import { useMutation, useQuery } from '@apollo/client/react'
import {
  Badge,
  Button,
  CollapsibleSection,
  ErrorState,
  FailureNotice,
  FieldMessage,
  InlineConfirmation,
  LiveRegion,
  LoadingState,
  MarkdownUploadArea,
  Notice,
  PageLayout,
  Section,
  Tabs,
  type MarkdownUploadState,
  buttonClasses,
  useLanguage,
  useTranslate,
} from '@athena/ui'
import { Link, useParams } from '@tanstack/react-router'
import { useRef, useState } from 'react'

import { usePageTitle } from '../app/page-title.js'
import { ArchiveAction } from '../features/practices/ArchiveAction.js'
import { SettingsPanel } from '../features/practices/SettingsPanel.js'
import { AssignPanel } from '../features/practices/AssignPanel.js'
import { MarkdownEditor, type MarkdownEditorHandle } from '../features/practices/MarkdownEditor.js'
import { QuestionList } from '../features/practices/QuestionList.js'
import { StudentPreview } from '../features/practices/StudentPreview.js'
import {
  PracticeDocument,
  CreatePracticeVersionDocument,
  PublishPracticeDocument,
  UpdatePracticeDraftDocument,
} from '../graphql/generated/operations.js'
import { useFormat } from '../lib/format.js'
import { useUserErrorText } from '../lib/user-errors.js'
import { practiceStatus } from '../lib/practice-status.js'

type Tab = 'questions' | 'preview'

/**
 * One practice: review it, replace it, publish it, assign it.
 *
 * This is the "Review → Preview → Assign → Publish" half of the handoff's
 * create flow. There is no per-question editor, and that is a V1 decision
 * rather than an omission: authoring is Markdown only (V1.md section 18), so
 * changing a question means replacing the document and parsing it again.
 *
 * The draft and the published version are shown as what they are. Editing
 * never touches a published version; it writes a draft, and publishing is the
 * single transition (V1.md section 9).
 */
export function PracticeScreen() {
  const t = useTranslate()
  const userErrorText = useUserErrorText()
  const format = useFormat()
  const language = useLanguage()
  const { practiceId } = useParams({ from: '/$language/tutor/practices/$practiceId' })
  const editorRef = useRef<MarkdownEditorHandle>(null)

  const { data, loading, error } = useQuery(PracticeDocument, { variables: { id: practiceId } })
  const [publish, { loading: publishing }] = useMutation(PublishPracticeDocument, {
    refetchQueries: [PracticeDocument],
    awaitRefetchQueries: true,
  })
  const [updateDraft, { loading: updating }] = useMutation(UpdatePracticeDraftDocument, {
    refetchQueries: [PracticeDocument],
    awaitRefetchQueries: true,
  })
  const [createVersion, { loading: creatingVersion }] = useMutation(CreatePracticeVersionDocument, {
    refetchQueries: [{ query: PracticeDocument, variables: { id: practiceId } }],
    awaitRefetchQueries: true,
  })

  const [tab, setTab] = useState<Tab>('questions')
  const [source, setSource] = useState('')
  const [uploadState, setUploadState] = useState<MarkdownUploadState>({ status: 'idle' })
  const [announcement, setAnnouncement] = useState('')
  const [publishError, setPublishError] = useState<string>()
  /** Set when a request never got an answer: said beside the action, with a retry, until it does. */
  const [publishFailure, setPublishFailure] = useState<unknown>()
  const [draftFailure, setDraftFailure] = useState<unknown>()
  const [versionError, setVersionError] = useState<string>()
  const [versionFailure, setVersionFailure] = useState<unknown>()
  /** Confirmed in place, because the tutor stays on the practice. */
  const [confirmed, setConfirmed] = useState<'published' | 'draftUpdated'>()

  usePageTitle(data?.practice?.title ?? t('practice.title'))

  // Guarded on there being nothing to show rather than on the query's state.
  // Assigning a student refetches this query, and Apollo 4 reports a refetch
  // as `loading` — so swapping the page for a loading line here threw away
  // every open section, every half-filled settings field and the confirmation
  // the tutor had just earned, on a request they did not ask for. The same
  // goes for `error`: a refetch that failed should not replace a page that is
  // already on screen.
  if (loading && !data?.practice) return <LoadingState>{t('practice.loadingOne')}</LoadingState>
  if (error && !data?.practice) return <ErrorState>{t('practice.loadError')}</ErrorState>
  if (!data?.practice) return <ErrorState>{t('practice.notFound')}</ErrorState>

  const practice = data.practice
  const shown = practice.draft ?? practice.publishedVersion
  const assignedIds = new Set(practice.assignedStudents.map((student) => student.id))

  const errorLines =
    uploadState.status === 'failed'
      ? uploadState.errors.flatMap((problem) => (problem.line === null ? [] : [problem.line]))
      : []

  async function onReplace() {
    setUploadState({ status: 'parsing' })
    setConfirmed(undefined)
    setAnnouncement('')
    let payload
    try {
      payload = (await updateDraft({ variables: { practiceId, markdown: source } })).data
        ?.updatePracticeDraft
    } catch (error) {
      // The Markdown stays in the editor, exactly as it was, for the retry.
      setUploadState({ status: 'idle' })
      setDraftFailure(error)
      return
    }
    setDraftFailure(undefined)

    if (payload?.practice) {
      setUploadState({ status: 'idle' })
      setSource('')
      setConfirmed('draftUpdated')
      return
    }

    const problems = payload
      ? payload.userErrors.map((error) => ({
          code: error.code,
          message: userErrorText(error),
          line: error.line,
        }))
      : [{ code: 'UNKNOWN', message: t('practice.unreadable'), line: null }]
    setUploadState({ status: 'failed', errors: problems })
    setAnnouncement(
      problems.length === 1 ? t('count.problemsOne') : t('count.problemsOther', [problems.length]),
    )
  }

  /**
   * `retry` is a second press after a publish that never got an answer. If
   * that first request did land, there is no draft left, and the server says
   * NO_DRAFT — which, on a retry, means the practice is published, not that
   * something went wrong.
   */
  async function onPublish(retry = false) {
    setPublishError(undefined)
    setConfirmed(undefined)
    let payload
    try {
      payload = (await publish({ variables: { practiceId } })).data?.publishPractice
    } catch (error) {
      setPublishFailure(error)
      return
    }
    setPublishFailure(undefined)
    const userError = payload?.userErrors[0]

    if (userError && !(retry && userError.code === 'NO_DRAFT')) {
      setPublishError(userErrorText(userError))
      return
    }
    if (!payload) {
      setPublishError(t('practice.publishFailed'))
      return
    }
    setConfirmed('published')
  }

  async function onCreateVersion() {
    setVersionError(undefined)
    setVersionFailure(undefined)
    try {
      const payload = (await createVersion({ variables: { practiceId } })).data
        ?.createPracticeVersion
      if (payload?.practice) {
        setAnnouncement(t('practice.newVersion.created'))
        return
      }
      setVersionError(
        payload?.userErrors[0]
          ? userErrorText(payload.userErrors[0])
          : t('practice.newVersion.failed'),
      )
    } catch (error) {
      setVersionFailure(error)
    }
  }

  return (
    <PageLayout width="wide">
      {/* The practice identity and lifecycle actions belong to the whole
          document, so they span both desktop regions. The shell already owns
          the semantic h1; this visible title is supporting page identity. */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <p className="text-h2 font-semibold text-text text-balance">{practice.title}</p>

        {/* The practice's lifecycle action, and only that, beside its title:
            Publish is the one thing this header asks for, so it is the one
            primary button in it. Archiving moved to the status block below —
            still the first thing under the header, but read as management of
            the practice rather than as a rival answer to "what now?". */}
        <div className="flex flex-col items-end gap-2">
          <div className="flex flex-wrap items-center justify-end gap-3">
            {/* Absent rather than disabled when there is nothing to publish.
                A greyed-out button is a question the tutor has to answer —
                "why can't I press this?" — and the answer is already on the
                line above it: this practice is published, version and all. It
                comes back the moment an edit opens a draft. */}
            {practice.draft ? (
              <Button
                size="lg"
                onClick={() => void onPublish()}
                loading={publishing}
                loadingText={t('practice.publishing')}
              >
                {t('practice.publish')}
              </Button>
            ) : null}
          </div>
          {publishError ? <FieldMessage tone="error">{publishError}</FieldMessage> : null}
          {publishFailure === undefined ? null : (
            <FailureNotice
              title={t('practice.publishFailed')}
              error={publishFailure}
              retrying={publishing}
              onRetry={() => void onPublish(true)}
            />
          )}
          {/* Where the button was: publishing takes it away, so the
              confirmation is what is left in its place. */}
          {confirmed === 'published' ? (
            <InlineConfirmation>{t('practice.published')}</InlineConfirmation>
          ) : null}
        </div>
      </div>

      {/* DOM order remains contextual information before authored content, so
          the narrow layout reads exactly as it did before the desktop rail.
          Only the desktop visual order changes. Both regions keep natural
          height: the application work area remains the sole scroll owner. */}
      <div className="grid min-w-0 grid-cols-1 gap-6 md:gap-8 desktop:grid-cols-[minmax(0,2fr)_minmax(18rem,1fr)] desktop:items-start">
        <aside className="order-1 flex min-w-0 flex-col gap-6 md:gap-8 desktop:order-2">
          <div className="flex flex-col items-start gap-2 border-b border-border pb-6">
            <Badge
              status={
                practice.archived
                  ? 'archived'
                  : practice.suspended
                    ? 'setAside'
                    : practiceStatus(practice)
              }
            />
            {/* What the practice is, before how big it is. Secondary text, so
                the title above stays the most prominent thing here. */}
            <p className="text-body-sm text-text-secondary">{format.categoryLine(practice)}</p>
            <p className="text-body-sm text-text-secondary">
              {shown
                ? format.count('questions', shown.questionCount)
                : t('practice.noQuestionsYet')}
              {practice.publishedVersion
                ? t('practice.publishedVersion', [practice.publishedVersion.versionNumber])
                : ''}
              {practice.draft ? t('practice.unpublishedChanges') : ''}
            </p>
            {/* The whole access state stays visible without opening settings. */}
            <p className="text-body-sm text-text-secondary">{format.accessSummary(practice)}</p>
            {/* Where the practice's state is described, so putting it away is
                found beside what it changes. First in narrow document order,
                directly under the header, and top of the rail at desktop. */}
            <div className="pt-2">
              <ArchiveAction
                practiceId={practice.id}
                archived={practice.archived}
                title={practice.title}
                assignedCount={practice.assignedStudents.length}
                hasDraft={Boolean(practice.draft)}
              />
            </div>
          </div>

          {practice.archived ? (
            <Notice title={t('practice.meta.archived')}>{t('practice.archivedNotice')}</Notice>
          ) : null}

          {/* Archived says enough on its own: restoring from the archive is the
              tutor's first step either way. A practice only the plan holds
              back is brought back from the plan screen. */}
          {practice.suspended && !practice.archived ? (
            <Notice
              title={t('practice.setAsideHeading')}
              actions={
                <Link
                  to="/$language/plan"
                  params={{ language }}
                  hash="practices-set-aside"
                  className={buttonClasses({ variant: 'secondary', size: 'sm' })}
                >
                  {t('subscription.bringBackOnPlan')}
                </Link>
              }
            >
              {t('practice.setAsideNotice')}
            </Notice>
          ) : null}

          {practice.archived && practice.publishedVersion && !practice.draft ? (
            <div className="flex flex-col items-start gap-3">
              <p className="text-body-sm text-text-secondary">
                {t('practice.newVersion.description')}
              </p>
              <Button
                onClick={() => void onCreateVersion()}
                loading={creatingVersion}
                loadingText={t('practice.newVersion.creating')}
              >
                {t('practice.newVersion.action')}
              </Button>
              {versionError ? <FieldMessage tone="error">{versionError}</FieldMessage> : null}
              {versionFailure === undefined ? null : (
                <FailureNotice
                  title={t('practice.newVersion.failed')}
                  error={versionFailure}
                  retrying={creatingVersion}
                  onRetry={() => void onCreateVersion()}
                />
              )}
            </div>
          ) : null}

          {/* Two folded sections rather than two open panels, so the page opens
              on the practice itself. Settings remains first in narrow document
              order and starts open only while an unpublished practice is being
              set up. The disclosure owns its one closing divider. */}
          <CollapsibleSection
            headingLevel={2}
            title={t('practice.settingsHeading')}
            defaultOpen={practice.publishedVersion === null}
          >
            <SettingsPanel practice={practice} layout="contextual-rail" />
          </CollapsibleSection>

          {/* Not shown until something is published, because there is nothing
              to attempt. The published version is server lifecycle state, not
              a client inference. */}
          {practice.publishedVersion ? (
            <CollapsibleSection
              headingLevel={2}
              title={t('practice.assignedTo')}
              meta={format.count('students', practice.assignedStudents.length)}
            >
              <AssignPanel
                practiceId={practice.id}
                students={data.myStudents}
                assignedIds={assignedIds}
                canAssign
              />
            </CollapsibleSection>
          ) : null}
        </aside>

        <div className="order-2 flex min-w-0 flex-col gap-6 md:gap-8 desktop:order-1">
          <Section
            id="questions"
            title={practice.draft ? t('practice.draftQuestions') : t('practice.questions')}
          >
            {!shown || shown.questions.length === 0 ? (
              <p className="text-body text-text-secondary">{t('practice.noQuestionsReplace')}</p>
            ) : (
              /* Inside the branch that has questions: a tab list controlling
                 an empty panel is two choices between the same nothing. */
              <Tabs
                label={t('practice.viewTabsLabel')}
                items={[
                  { value: 'questions', label: t('practice.viewWithAnswers') },
                  { value: 'preview', label: t('practice.viewAsStudent') },
                ]}
                value={tab}
                onValueChange={setTab}
              >
                {tab === 'questions' ? (
                  <QuestionList
                    questions={shown.questions}
                    editable={Boolean(practice.draft)}
                    practiceId={practice.id}
                  />
                ) : (
                  <StudentPreview questions={shown.questions} />
                )}
              </Tabs>
            )}
          </Section>

          {practice.draft ? (
            <CollapsibleSection headingLevel={2} title={t('practice.replaceHeading')}>
              <div className="flex flex-col gap-4">
                <p className="text-body-sm text-text-secondary">
                  {t('practice.replaceDescription')}
                </p>

                <MarkdownUploadArea
                  state={uploadState}
                  onMarkdown={(text) => {
                    setSource(text)
                    setUploadState({ status: 'idle' })
                  }}
                  onFixError={(line) => editorRef.current?.focusLine(line)}
                />

                <MarkdownEditor
                  ref={editorRef}
                  value={source}
                  onChange={setSource}
                  errorLines={errorLines}
                />

                <div>
                  <Button
                    onClick={() => void onReplace()}
                    loading={updating}
                    loadingText={t('common.saving')}
                    disabled={source.trim().length === 0}
                  >
                    {t('practice.parseAndSaveDraft')}
                  </Button>
                </div>
                {draftFailure === undefined ? null : (
                  <FailureNotice
                    title={t('practice.draftFailed')}
                    error={draftFailure}
                    retrying={updating}
                    onRetry={() => void onReplace()}
                  />
                )}
                {confirmed === 'draftUpdated' ? (
                  <InlineConfirmation className="self-start">
                    {t('practice.draftUpdated')}
                  </InlineConfirmation>
                ) : null}
              </div>
            </CollapsibleSection>
          ) : null}

          <Section id="versions" title={t('practice.versionHistory')}>
            <ul className="divide-y divide-border">
              {practice.versions.map((version) => (
                <li
                  key={version.id}
                  className="flex flex-wrap items-center justify-between gap-3 py-4 first:pt-0 last:pb-0"
                >
                  <span className="text-body text-text">
                    {t('practice.version', [version.versionNumber])}
                  </span>
                  <span className="flex items-center gap-3 text-body-sm text-text-secondary">
                    {version.publishedAt
                      ? format.date(version.publishedAt)
                      : t('practice.notPublished')}
                    <Badge status={version.status === 'PUBLISHED' ? 'published' : 'draft'} />
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        </div>
      </div>

      <LiveRegion message={announcement} />
    </PageLayout>
  )
}
